import { useEffect, useRef, useState } from 'react';
import { caughtErrorMessage, serverErrorMessage } from '../i18n/server-errors';
import { t } from '../services/language-manager';
import { registerResumeDraft, resumeDraft } from '../services/reauth-resume';
import { saveOrgSettings } from '../services/wizard-backend';
import { SaveButton, SaveRow, useSaveConfirmation } from './SaveFeedback';

interface CallDraft {
  transcription: boolean;
  exportOn: boolean;
}

interface CallTranscriptionSectionProps {
  genesysOrgId: string;
  genesysRegion: string;
  /** Stored value. Missing on the organization reads as on. */
  transcriptionEnabled: boolean;
  /** Stored value. Missing on the organization reads as on. */
  exportEnabled: boolean;
  /** Dev preview: keep the payload on the section instead of calling the API. */
  preview?: boolean;
}

/**
 * Live call transcription on the agent's call overview, and PDF export of that
 * transcript. Each save sends only the flag that changed, so threat email,
 * reply languages and external-tag search are left as stored.
 */
export function CallTranscriptionSection({
  genesysOrgId,
  genesysRegion,
  transcriptionEnabled,
  exportEnabled,
  preview = false,
}: CallTranscriptionSectionProps) {
  const [transcription, setTranscription] = useState(() => {
    const draft = resumeDraft<CallDraft>('settings-call-tx');
    return draft && typeof draft.transcription === 'boolean' ? draft.transcription : transcriptionEnabled;
  });
  const [exportOn, setExportOn] = useState(() => {
    const draft = resumeDraft<CallDraft>('settings-call-tx');
    return draft && typeof draft.exportOn === 'boolean' ? draft.exportOn : exportEnabled;
  });
  const [persistedTx, setPersistedTx] = useState(transcriptionEnabled);
  const [persistedExport, setPersistedExport] = useState(exportEnabled);
  const kept = useRef({ transcription, exportOn, persistedTx, persistedExport });
  kept.current = { transcription, exportOn, persistedTx, persistedExport };

  useEffect(() => registerResumeDraft('settings-call-tx', () => {
    const cur = kept.current;
    if (cur.transcription === cur.persistedTx && cur.exportOn === cur.persistedExport) return null;
    return { transcription: cur.transcription, exportOn: cur.exportOn };
  }), []);

  const [saving, setSaving] = useState(false);
  const saveConfirmation = useSaveConfirmation();
  const [saveError, setSaveError] = useState<string | null>(null);
  const [savedPayload, setSavedPayload] = useState<string | null>(null);
  const dirty = transcription !== persistedTx || exportOn !== persistedExport;

  const handleSave = async () => {
    const body: { callTranscriptionEnabled?: boolean; callTranscriptExportEnabled?: boolean } = {};
    if (transcription !== persistedTx) body.callTranscriptionEnabled = transcription;
    if (exportOn !== persistedExport) body.callTranscriptExportEnabled = exportOn;
    setSaving(true);
    saveConfirmation.clear();
    setSaveError(null);
    try {
      if (preview) {
        setPersistedTx(transcription);
        setPersistedExport(exportOn);
        setSavedPayload(JSON.stringify(body));
        saveConfirmation.confirm(t('settings-save-success'));
        return;
      }
      const result = await saveOrgSettings(genesysOrgId, genesysRegion, body);
      if (result.status) {
        setPersistedTx(transcription);
        setPersistedExport(exportOn);
        saveConfirmation.confirm(t('settings-save-success'));
        return;
      }
      setSaveError(serverErrorMessage({
        code: result.code,
        cause: result.cause,
        knownKey: result.code ? undefined : 'settings-save-error',
        keepDetail: true,
      }));
    } catch (e) {
      setSaveError(caughtErrorMessage(e, 'settings-save-error'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <section
      id="call-transcription"
      className="settings-section"
      {...(preview && savedPayload ? { 'data-saved-call-transcription': savedPayload } : {})}
    >
      <h3 className="settings-section__title">{t('settings-call-tx-title')}</h3>
      <div className="form-group">
        <label className="settings-page__toggle">
          <input
            type="checkbox"
            checked={transcription}
            aria-describedby="call-transcription-help"
            onChange={(e) => {
              setTranscription(e.target.checked);
              saveConfirmation.clear();
              setSaveError(null);
            }}
          />
          <span>{t('settings-call-tx-enable')}</span>
        </label>
        <p className="form-hint" id="call-transcription-help">{t('settings-call-tx-help')}</p>
      </div>
      <div className="form-group">
        <label className="settings-page__toggle">
          <input
            type="checkbox"
            checked={exportOn}
            aria-describedby="call-transcript-export-help"
            onChange={(e) => {
              setExportOn(e.target.checked);
              saveConfirmation.clear();
              setSaveError(null);
            }}
          />
          <span>{t('settings-call-tx-export')}</span>
        </label>
        <p className="form-hint" id="call-transcript-export-help">{t('settings-call-tx-export-help')}</p>
      </div>

      <SaveRow className="settings-section__save" error={saveError} message={saveConfirmation.message}>
        <SaveButton
          saving={saving}
          justSaved={saveConfirmation.justSaved}
          label={t('settings-save')}
          savingLabel={t('settings-saving')}
          onClick={handleSave}
          disabled={!dirty}
        />
      </SaveRow>
    </section>
  );
}
